import type {
  AccountCardWire,
  AccountDetailPayload,
  SpendingCardView,
  SpendingLinkedSection,
  SpendingLinkedView,
  SpendingSummaryView,
} from '../account-detail.model';
import { formatAccountBalanceExact, formatAccountLast4 } from '../accounts-display';

const NOT_AVAILABLE = 'Not available';

/** `DIGITAL_ACTIVE` → `Digital Active`. Empty → null (caller uses Not available). */
export function formatCardCurrentState(state: string | null | undefined): string | null {
  const trimmed = state?.trim();
  if (!trimmed) {
    return null;
  }
  const label = trimmed
    .split(/_+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
  return label || null;
}

function cardViews(payload: AccountDetailPayload): SpendingCardView[] {
  const wires: AccountCardWire[] = [];
  if (Array.isArray(payload.cards) && payload.cards.length > 0) {
    wires.push(...payload.cards);
  } else if (payload.activeCard) {
    wires.push(payload.activeCard);
  }
  return wires.map((card) => ({
    chipLabel: formatCardCurrentState(card.currentState) ?? NOT_AVAILABLE,
    last4Label: formatAccountLast4(card.cardNumberMasked),
  }));
}

function linkedViews(payload: AccountDetailPayload): SpendingLinkedSection {
  if (!Array.isArray(payload.linkedAccounts)) {
    return { show: false, empty: false, items: [] };
  }
  const list = payload.linkedAccounts;
  const items: SpendingLinkedView[] = list.map((account) => ({
    chipLabel: account.status?.trim() || NOT_AVAILABLE,
    name: (account.nickName ?? account.name ?? '').trim() || NOT_AVAILABLE,
    last4Label: formatAccountLast4(account.idMasked ?? account.accountNumberMasked),
  }));
  return {
    show: true,
    empty: items.length === 0,
    items,
  };
}

/** Presentational view-model. Never includes accountNumber or routingNumber. */
export function mapSpendingSummary(payload: AccountDetailPayload): SpendingSummaryView {
  return {
    availableBalance: formatAccountBalanceExact(payload.balance),
    cards: cardViews(payload),
    linkedAccounts: linkedViews(payload),
  };
}
